import { useMemo } from 'react';
import { Card } from '@components/ui';
import { ChevronDown, Building2, TrendingUp, Wallet, ArrowRight } from 'lucide-react';
import clsx from 'clsx';
import FinBlockAccordion from './FinBlokListesi';
import { getBlocksByConstructionId } from '@mocks/bloklar';

/**
 * FinProjeKarti — Finansal Proje Özeti
 * 
 * İnşaat özet kartının finansal versiyonu.
 * Progress bar fiziksel ilerleme yerine TAHSİLAT oranını gösterir.
 */
export default function FinProjeKarti({
    insaat, isExpanded, onToggle, expandedBlok, setExpandedBlok,
    filterDurum, searchTerm, navigate, formatCurrency, stats
}) {
    // Projeye ait blokları getir
    const bloklar = useMemo(() => getBlocksByConstructionId(insaat.id), [insaat.id]);

    // Tahsilat Oranı (Finansal İlerleme)
    const tahsilatOrani = stats.beklenenGelir > 0
        ? Math.round((stats.toplamTahsilat / stats.beklenenGelir) * 100)
        : 0;

    const getProgressColor = (y) => y >= 75 ? 'bg-success' : y >= 40 ? 'bg-primary' : y > 0 ? 'bg-warning' : 'bg-bg-tertiary';
    const getProgressTextColor = (y) => y >= 75 ? 'text-success' : y >= 40 ? 'text-primary' : y > 0 ? 'text-warning' : 'text-text-muted';

    return (
        <Card padding="none" className={clsx('overflow-hidden transition-all border border-border-light', isExpanded && 'ring-1 ring-primary/20 shadow-md')}>
            {/* Üst progress çizgi (Tahsilat) */}
            <div className="h-1.5 bg-bg-tertiary w-full">
                <div
                    className={clsx('h-full transition-all duration-500', getProgressColor(tahsilatOrani))}
                    style={{ width: `${tahsilatOrani}%` }}
                />
            </div>

            {/* Header Kısmı */}
            <div className="flex flex-col md:flex-row">
                <button
                    onClick={onToggle}
                    className="flex-1 flex items-center gap-4 p-4 md:p-5 text-left hover:bg-bg-hover transition-colors"
                >
                    {/* İkon */}
                    <div className="hidden sm:flex w-12 h-12 rounded-xl bg-primary/10 items-center justify-center shrink-0">
                        <Building2 size={24} className="text-primary" />
                    </div>

                    {/* Proje Bilgisi */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-bold text-lg text-text-primary">{insaat.name}</h3>
                            <span className={clsx('px-2.5 py-0.5 rounded-full text-xs font-semibold bg-bg-tertiary text-text-secondary')}>
                                {bloklar.length} Blok
                            </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-text-secondary">
                            <span>{insaat.location}</span>
                            <span className="text-text-muted">•</span>
                            <span>{stats.toplamSatilan}/{stats.toplamDaire} Daire Satıldı</span>
                        </div>
                    </div>

                    {/* Finansal Özet (Masaüstü) */}
                    <div className="hidden md:flex flex-col items-end gap-1 mr-4 text-right">
                        <div className="text-xs text-text-muted font-medium uppercase tracking-wide">Beklenen Gelir</div>
                        <div className="text-lg font-bold text-primary">{formatCurrency(stats.beklenenGelir)}</div>
                        <div className="flex items-center gap-2 text-xs">
                            <span className="text-success font-medium">Tahsilat: {formatCurrency(stats.toplamTahsilat)}</span>
                            <span className="text-text-muted">|</span>
                            <span className="text-warning font-medium">Kalan: {formatCurrency(stats.kalanAlacak)}</span>
                        </div>
                    </div>

                    {/* Expand İkonu */}
                    <div className="pl-2 border-l border-border-light md:border-0 md:pl-0">
                        <ChevronDown size={20} className={clsx('text-text-muted transition-transform duration-300', isExpanded && 'rotate-180')} />
                    </div>
                </button>
            </div>

            {/* Mobil Finansal Özet (Sadece mobilde görünür) */}
            <div className="md:hidden flex border-t border-border-light divide-x divide-border-light bg-bg-tertiary/20">
                <div className="flex-1 p-3 text-center">
                    <div className="text-2xs text-text-muted uppercase">Satış</div>
                    <div className="text-sm font-bold text-primary">{formatCurrency(stats.beklenenGelir)}</div>
                </div>
                <div className="flex-1 p-3 text-center">
                    <div className="text-2xs text-text-muted uppercase">Tahsilat</div>
                    <div className="text-sm font-bold text-success">{formatCurrency(stats.toplamTahsilat)}</div>
                </div>
                <div className="flex-1 p-3 text-center">
                    <div className="text-2xs text-text-muted uppercase">Kalan</div>
                    <div className="text-sm font-bold text-warning">{formatCurrency(stats.kalanAlacak)}</div>
                </div>
            </div>

            {/* Expanded Content - Blok Listesi */}
            {isExpanded && (
                <div className="border-t border-border-light bg-bg-secondary/30 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="p-4 md:p-6 space-y-4">
                        {bloklar.map(blok => (
                            <FinBlockAccordion
                                key={blok.id}
                                blok={blok}
                                insaatId={insaat.id}
                                isExpanded={expandedBlok === blok.id}
                                onToggle={() => setExpandedBlok(expandedBlok === blok.id ? null : blok.id)}
                                formatCurrency={formatCurrency}
                                filterDurum={filterDurum}
                                searchTerm={searchTerm}
                            />
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
}
